<template>
  <div class="ui segment">
    <div class="centered-header">
      <h2 class="ui header" style="color: #092442;">
        <i class="clipboard list icon"></i>
        <div class="content">
          Reservation Information
        </div>
      </h2>
    </div>
    <div class="ui divider"></div>
    <div v-if="loading" class="ui active centered inline loader">
      <div class="ui text loader">{{ $t('loading') }}...</div>
    </div>

    <div v-else class="ui form" id="reservation-info-step">
      <div class="required field" v-if="getVisitRequiredSource() === 'doctor'">
        <label for="reservation_type">Choose Doctor</label>
        <select
          class="ui fluid search dropdown"
          id="doctor-search"
          v-model="selected_doctor_id"
          name="doctor"
        >
          <option value="">Choose Doctor</option>
          <option :value="doctor.id" v-for="doctor in doctors" :key="doctor.id">
            {{ doctor.user.name }}
          </option>
        </select>
      </div>

      <div class="required field">
        <label for="packages">Packages</label>
        <select
          name="packages"
          multiple="[]"
          class="ui fluid search dropdown"
          id="packages-multi-select"
          v-model="packagesIds"
        >
          <option value="">Packages</option>
          <option
            :value="reservationPackage.id"
            v-for="reservationPackage in packages"
            :key="reservationPackage.id"
          >
            <!-- {{ reservationPackage.name }} -->
            {{getPackageName(reservationPackage)}}
          </option>
        </select>
      </div>
      <div v-if="resPackages.length">
        <div class="ui divider"></div>
        <div class="field" style="">
          <table
            class="ui center aligned celled very compact table"
            style="width: 100%"
          >
            <thead>
              <tr>
                <th>Package Name</th>
                <!-- <th>Repeat</th> -->
                <th>Repeat</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(selectedPackage, index) in resPackages" :key="index">
                <td>{{getPackageName(selectedPackage)}}</td>
                <!-- <td>{{ selectedPackage.quantity }}</td> -->
                <td>
                  <div class="ui fluid input focus">
                    <input
                      type="number"
                      style="text-align: center"
                      :placeholder="'Repeated Quantity'"
                      v-model="selectedPackage.quantity"
                      :name="index"
                      :min="1"
                      step="1"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="ui divider"></div>
      <div
        v-if="canGoBack"
        class="ui blue left labeled icon button"
        style="background-color: #092442"
        @click="previous()"
      >
        <i class="left arrow icon"></i>
        Previous
      </div>

      <div
        class="ui blue right floated right labeled icon button"
        style="background-color: #092442"
        :class="{ disabled: !canGoNext() }"
        @click="next()"
        v-if="getVisitRequiredSource() === 'doctor'"
      >
        <i class="right arrow icon"></i>
        Next
      </div>
      <div
        class="ui green right floated right labeled icon button"
        :class="{ disabled: !canGoNext() }"
        @click="skipVisits()"
      >
        <i class="right arrow icon"></i>
        Skip Visits
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    canGoBack: { type: Boolean, default: true },
    doctor_id: { type: Number, required: false },
    reservationPackages: { type: Array, required: true },
    reservationId: { type: Number, default: null }
  },
  data() {
    return {
      doctors: [],
      packages: [],
      selected_doctor_id: this.doctor_id,
      loading: true,
      packagesIds: [],
      resPackages: this.reservationPackages,
      lastDoctorId: 32
    };
  },
  async created() {
    await this.axios.all([this.getDoctorsList(), this.getPackagesList()]).then(
      this.axios.spread((doctorsResponse, packagesResponse) => {
        this.doctors = doctorsResponse.data;
        this.packages = packagesResponse.data;
        this.loading = false;
        this.lastDoctorId = this.doctors[this.doctors.length - 1].id;
        this.packagesIds = this.reservationPackages.map(el => el.id);
        this.initGUI();
      })
    );
  },
  watch: {
    packagesIds: {
      handler: function() {
        // for each of the packages chosen by the user
        let reservationPackagesIds = this.reservationPackages.map(
          rPackage => rPackage.id
        );
        this.packagesIds.forEach(packageId => {
          // if not set in the reservation packages
          if (this.resPackages.filter(c => c.id === packageId).length === 0) {
            // find the package in the available packages
            const p = reservationPackagesIds.includes(packageId)
              ? this.reservationPackages.find(p => p.id === packageId)
              : this.packages.find(p => p.id === packageId);

            if (p && !reservationPackagesIds.includes(packageId)) {
              this.resPackages.push({
                id: p.id,
                entities: p.entities,
                name: p.name,
                quantity: 1, // number of instances from this packages
                units: p.quantity, // package number of units
                unit_price: p.unit_price,
                unit_measurement_id: p.unit_measurement_id,
                description: p.description,
                disabled: false
              });
            } else {
              this.resPackages.push(p);
            }
          }
        });

        this.resPackages = this.resPackages.filter(c =>
          this.packagesIds.includes(c.id)
        );
      },
      deep: true
    }
  },
  methods: {
    getDoctorsList() {
      return this.$http.get('doctors/list');
    },
    getPackageName(selectedPackage) {
      let quantity = selectedPackage.units ? selectedPackage.units : selectedPackage.quantity
      let packageName = selectedPackage.name + ' :: ' + quantity + ' with ' 
        + (this.formatPrice(Number(quantity) * Number(selectedPackage.unit_price)))

      if (selectedPackage.entities && selectedPackage.entities[0]) {
        packageName = selectedPackage.entities[0].name + ' :: ' + packageName
      }

      return packageName
    },
    getPackagesList() {
      let packagesURL = 'packages/available';
      if (this.reservationId) {
        packagesURL = `reservations/${this.reservationId}/` + packagesURL;
      }

      return this.$http.get(packagesURL, {
        params: {
          clinic_id: this.getClinicId()
        }
      });
    },
    handleQuantity(event, index) {  
      if (event.target.value < 1) {
        event.target.value = 1
      }
    },
    initGUI() {
      this.$nextTick(() => {
        $('#packages-multi-select').dropdown();
        $('#doctor-search').dropdown();
      });
    },
    next() {
      const selectedDoctor = this.doctors.find(
        el => el.id === this.selected_doctor_id
      );

      this.$emit('reservation-reservationStepChanges', {
        data: {
          reservationPackages: this.resPackages,
          doctor: selectedDoctor,
          next: this.canGoNext()
        }
      });
    },
    skipVisits() {
      const selectedDoctor = this.doctors.find(
        el => el.id === this.selected_doctor_id
      );

      this.$emit('reservation-reservationStepChanges-skipVisits', {
        data: {
          reservationPackages: this.resPackages,
          doctor: selectedDoctor,
          next: this.canGoNext(),
        }
      });
    },
    previous() {
      this.$emit('reservation-goBack');
    },
    canGoNext() {
      let wrongPackageQuantity = false 
      this.resPackages.forEach(selectedPackage => {
        if (selectedPackage.quantity < 1 || !selectedPackage.quantity) {
          wrongPackageQuantity = true
        }
      })
      return this.packagesIds.length > 0 && !wrongPackageQuantity;
    }
  }
};
</script>

<style scoped>
.centered-header {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/steps/reservations/new_reservations_steps/ReservationInfoStep.vue